import { useEffect, useRef, useState } from 'react'
import {
  buildPlayerPayload,
  type PlayerAcceptPayload,
} from '@sportninja/common/features/invitation/helper/buildPlayerPayload'

export type UnderageStep = 'who' | 'review' | 'notEligible' | 'accept'

export interface UseUnderageInviteFlowArgs {
  playerId: string
  onSubmit: (player: PlayerAcceptPayload) => void
  initial?: Partial<{
    nameFirst: string
    nameLast: string
    birthDate: string
  }>
}

export interface UseUnderageInviteFlowResult {
  step: UnderageStep
  radioOption: 'yes' | 'no'
  setRadioOption: (v: string) => void
  relationship: string
  setRelationship: (v: string) => void
  nameFirst: string
  setNameFirst: (v: string) => void
  nameLast: string
  setNameLast: (v: string) => void
  birthDate: string
  setBirthDate: (v: string) => void
  heightFt: string
  setHeightFt: (v: string) => void
  heightIn: string
  setHeightIn: (v: string) => void
  weight: string
  setWeight: (v: string) => void
  fieldErrors: string[]
  customError: Record<string, string>
  fromWhoNext: () => void
  fromReviewBack: () => void
  fromReviewNext: () => void
  fromNotEligibleBack: () => void
  fromAcceptBack: () => void
  submit: () => void
}

export const useUnderageInviteFlow = ({
  playerId,
  onSubmit,
  initial,
}: UseUnderageInviteFlowArgs): UseUnderageInviteFlowResult => {
  const [step, setStep] = useState<UnderageStep>('who')
  const [radioOption, setRadioOptionState] = useState<'yes' | 'no'>('no')
  const [relationship, setRelationship] = useState('')
  const [nameFirst, setNameFirst] = useState(initial?.nameFirst ?? '')
  const [nameLast, setNameLast] = useState(initial?.nameLast ?? '')
  const [birthDate, setBirthDate] = useState(initial?.birthDate ?? '')
  const [heightFt, setHeightFt] = useState('')
  const [heightIn, setHeightIn] = useState('')
  const [weight, setWeight] = useState('')
  const [fieldErrors, setFieldErrors] = useState<string[]>([])
  const [customError, setCustomError] = useState<Record<string, string>>({})

  // Prefill name/birthDate from the invite once it loads. `initial` is empty on
  // the first render (invite data is still fetching), so seeding state only in
  // useState would leave the review step blank. Sync each field when `initial`
  // arrives — but only until the user edits it, so we never clobber their input.
  const userEditedRef = useRef(false)
  const markEdited = (setter: (v: string) => void) => (v: string) => {
    userEditedRef.current = true
    setter(v)
  }
  useEffect(() => {
    if (userEditedRef.current) {
      return
    }
    if (initial?.nameFirst) {
      setNameFirst(initial.nameFirst)
    }
    if (initial?.nameLast) {
      setNameLast(initial.nameLast)
    }
    if (initial?.birthDate) {
      setBirthDate(initial.birthDate)
    }
  }, [initial?.nameFirst, initial?.nameLast, initial?.birthDate])

  const setRadioOption = (v: string) =>
    setRadioOptionState(v === 'yes' ? 'yes' : 'no')

  const submit = () => {
    onSubmit(
      buildPlayerPayload({
        playerId,
        nameFirst,
        nameLast,
        birthDate,
        heightFt,
        heightIn,
        weight,
        relationship,
      })
    )
  }

  const fromWhoNext = () =>
    setStep(radioOption === 'yes' ? 'notEligible' : 'review')

  const fromReviewBack = () => setStep('who')
  const fromNotEligibleBack = () => setStep('who')
  const fromAcceptBack = () => setStep('review')

  const fromReviewNext = () => {
    const errors: string[] = []
    const custom: Record<string, string> = {}
    if (!nameFirst.trim()) {
      errors.push('player.name_first')
      custom['player.name_first'] = 'Required'
    }
    if (!nameLast.trim()) {
      errors.push('player.name_last')
      custom['player.name_last'] = 'Required'
    }
    if (!birthDate.trim()) {
      errors.push('player.birth_date')
      custom['player.birth_date'] = 'Required'
    }
    if (errors.length > 0) {
      setFieldErrors(errors)
      setCustomError(custom)
      return
    }
    setFieldErrors([])
    setCustomError({})
    // Always advance to the final accept step (the regular invite card with
    // NOT NOW / JOIN). Join there fires the single accept API — Next never does.
    // The waiver, when present, is shown inside that final step.
    setStep('accept')
  }

  return {
    step,
    radioOption,
    setRadioOption,
    relationship,
    setRelationship,
    nameFirst,
    setNameFirst: markEdited(setNameFirst),
    nameLast,
    setNameLast: markEdited(setNameLast),
    birthDate,
    setBirthDate: markEdited(setBirthDate),
    heightFt,
    setHeightFt,
    heightIn,
    setHeightIn,
    weight,
    setWeight,
    fieldErrors,
    customError,
    fromWhoNext,
    fromReviewBack,
    fromReviewNext,
    fromNotEligibleBack,
    fromAcceptBack,
    submit,
  }
}
